import { supabase, storage, toError } from '@/lib/supabase'
import type { Document, DocumentInsert, DocumentUpdate, DocumentStatus } from '@/types/database'
import type { DocumentVersion, DocumentVersionWithUploader } from '@/types/documentVersion'
import { profileService } from './profileService'
import { activityLogService } from './activityLogService'
import { captureError } from '@/lib/sentry'
import { DEFAULT_EXPIRY_WARNING_DAYS } from '@/lib/constants'

const DOCUMENTS_BUCKET = 'documents'

export type DocumentWithUsers = Document & {
  uploader?: { full_name: string | null } | null
  updater?: { full_name: string | null } | null
}

const DOC_SELECT = '*, uploader:profiles!documents_uploaded_by_fkey(full_name), updater:profiles!documents_updated_by_fkey(full_name)'

export const documentService = {
  /**
   * Get all documents for the current organization
   */
  async getDocuments(): Promise<DocumentWithUsers[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('documents')
      .select(DOC_SELECT)
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false })

    if (error) {
      // Fallback if updated_by FK not yet available
      if (error.message?.includes('updated_by')) {
        const { data: fallback } = await supabase
          .from('documents')
          .select('*, uploader:profiles!documents_uploaded_by_fkey(full_name)')
          .eq('organization_id', orgId)
          .order('created_at', { ascending: false })
        return fallback ?? []
      }
      console.error('Error fetching documents:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get a single document by ID
   * Note: Includes organization filtering as defense-in-depth alongside RLS
   */
  async getDocument(id: string): Promise<DocumentWithUsers | null> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) {
      console.warn('[DocumentService] No organization found for user')
      return null
    }

    const { data, error } = await supabase
      .from('documents')
      .select(DOC_SELECT)
      .eq('id', id)
      .eq('organization_id', orgId)
      .single()

    if (error) {
      console.error('Error fetching document:', error)
      return null
    }

    return data
  },

  /**
   * Create a new document record (without file upload)
   */
  async createDocument(document: Omit<DocumentInsert, 'organization_id'>): Promise<Document | null> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) throw new Error('No organization found')

    const { data: { user } } = await supabase.auth.getUser()

    const { data, error } = await supabase
      .from('documents')
      .insert({
        ...document,
        organization_id: orgId,
        uploaded_by: user?.id,
      })
      .select()
      .single()

    if (error) {
      console.error('Error creating document:', error)
      throw toError(error)
    }

    if (data) {
      activityLogService.log({
        action: 'created',
        entityType: 'document',
        entityId: data.id,
        entityName: data.name,
        details: { type: data.type, status: data.status },
      })
    }

    return data
  },

  /**
   * Upload a file and create document record
   */
  async uploadDocument(
    file: File,
    metadata: Omit<DocumentInsert, 'organization_id' | 'file_path' | 'file_size' | 'mime_type'>
  ): Promise<Document | null> {
    // Validate file type against allowlist
    const ALLOWED_MIME_TYPES = new Set([
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.ms-publisher',
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'text/plain',
      'text/csv',
      // CAD files
      'application/iges',
      'model/iges',
      'application/step',
      'model/step',
      'application/octet-stream', // Browsers often report CAD files as binary
    ])
    const ALLOWED_EXTENSIONS = new Set([
      'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'pub',
      'jpg', 'jpeg', 'png', 'gif', 'webp', 'txt', 'csv',
      'igs', 'iges', 'stp', 'step',
    ])

    const fileParts = file.name.split('.')
    const fileExt = (fileParts.length > 1 ? fileParts.pop() || '' : '').toLowerCase()

    if (!ALLOWED_EXTENSIONS.has(fileExt)) {
      throw new Error(`File type ".${fileExt}" is not allowed. Accepted types: PDF, Word, Excel, PowerPoint, Publisher, images, CSV, plain text, and CAD files (IGES, STEP).`)
    }
    if (file.type && !ALLOWED_MIME_TYPES.has(file.type)) {
      throw new Error(`MIME type "${file.type}" is not allowed. Please upload a supported document or image file.`)
    }

    const orgId = await profileService.getOrganizationId()
    if (!orgId) throw new Error('No organization found')

    const { data: { user } } = await supabase.auth.getUser()

    // Generate unique file path
    const fileName = `${orgId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`

    // Upload file to storage
    const { error: uploadError } = await storage.uploadDocument(DOCUMENTS_BUCKET, fileName, file)

    if (uploadError) {
      console.error('Error uploading file:', uploadError)
      throw toError(uploadError)
    }

    // Create document record
    const { data, error } = await supabase
      .from('documents')
      .insert({
        ...metadata,
        organization_id: orgId,
        file_path: fileName,
        file_size: file.size,
        mime_type: file.type,
        uploaded_by: user?.id,
      })
      .select()
      .single()

    if (error) {
      // Clean up uploaded file if record creation fails
      await storage.deleteDocument(DOCUMENTS_BUCKET, [fileName])
      console.error('Error creating document record:', error)
      throw toError(error)
    }

    if (data) {
      activityLogService.log({
        action: 'uploaded',
        entityType: 'document',
        entityId: data.id,
        entityName: data.name,
        details: { type: data.type, file_size: file.size, mime_type: file.type },
      })
    }

    return data
  },

  /**
   * Update a document
   * Note: Includes organization filtering as defense-in-depth alongside RLS
   */
  async updateDocument(id: string, updates: DocumentUpdate): Promise<Document | null> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) throw new Error('No organization found')

    const { data: { user } } = await supabase.auth.getUser()

    const { data, error } = await supabase
      .from('documents')
      .update({ ...updates, updated_by: user?.id })
      .eq('id', id)
      .eq('organization_id', orgId)
      .select()
      .single()

    if (error) {
      console.error('Error updating document:', error)
      throw toError(error)
    }

    if (data) {
      activityLogService.log({
        action: 'updated',
        entityType: 'document',
        entityId: data.id,
        entityName: data.name,
        details: { fields_changed: Object.keys(updates) },
      })
    }

    return data
  },

  /**
   * Delete a document and its file
   * Note: Includes organization filtering as defense-in-depth alongside RLS
   */
  async deleteDocument(id: string): Promise<boolean> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) throw new Error('No organization found')

    // Get document to find file path (already has org filtering)
    const document = await this.getDocument(id)

    // Delete record first
    const { error } = await supabase
      .from('documents')
      .delete()
      .eq('id', id)
      .eq('organization_id', orgId)

    if (error) {
      console.error('Error deleting document:', error)
      throw toError(error)
    }

    // Delete file from storage if exists
    if (document?.file_path) {
      await storage.deleteDocument(DOCUMENTS_BUCKET, [document.file_path])
    }

    activityLogService.log({
      action: 'deleted',
      entityType: 'document',
      entityId: id,
      entityName: document?.name,
    })

    return true
  },

  /**
   * Get download URL for a document
   */
  getDocumentUrl(filePath: string): string {
    const { data } = storage.getPublicUrl(DOCUMENTS_BUCKET, filePath)
    return data.publicUrl
  },

  /**
   * Download a document file
   */
  async downloadDocument(filePath: string): Promise<Blob | null> {
    const { data, error } = await storage.downloadDocument(DOCUMENTS_BUCKET, filePath)

    if (error) {
      console.error('Error downloading document:', error)
      return null
    }

    return data
  },

  /**
   * Get documents by product
   * Note: Includes organization filtering as defense-in-depth alongside RLS
   */
  async getDocumentsByProduct(productId: string): Promise<Document[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('documents')
      .select('*')
      .eq('product_id', productId)
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching documents by product:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get documents by component
   */
  async getDocumentsByComponent(componentId: string): Promise<Document[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('documents')
      .select('*')
      .eq('component_id', componentId)
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching documents by component:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get expiring documents (within X days)
   */
  async getExpiringDocuments(withinDays: number = DEFAULT_EXPIRY_WARNING_DAYS): Promise<Document[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const futureDate = new Date()
    futureDate.setDate(futureDate.getDate() + withinDays)

    const today = new Date().toISOString().split('T')[0]

    const { data, error } = await supabase
      .from('documents')
      .select('*')
      .eq('organization_id', orgId)
      .not('expiry_date', 'is', null)
      .gte('expiry_date', today)
      .lte('expiry_date', futureDate.toISOString().split('T')[0])
      .order('expiry_date')

    if (error) {
      console.error('Error fetching expiring documents:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Update document status based on expiry date
   */
  calculateDocumentStatus(expiryDate: string | null): DocumentStatus {
    if (!expiryDate) return 'valid'

    const expiry = new Date(expiryDate)
    const now = new Date()
    const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

    if (daysUntilExpiry < 0) return 'expired'
    if (daysUntilExpiry <= 30) return 'expiring'
    return 'valid'
  },

  /**
   * Get document stats for dashboard
   */
  async getDocumentStats(): Promise<{
    total: number
    expiring: number
    expired: number
    byType: Record<string, number>
  }> {
    const documents = await this.getDocuments()

    const byType = documents.reduce((acc, doc) => {
      const docType = doc.type || 'other'
      acc[docType] = (acc[docType] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    const now = new Date()
    const thirtyDaysFromNow = new Date()
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + DEFAULT_EXPIRY_WARNING_DAYS)

    return {
      total: documents.length,
      expiring: documents.filter(d =>
        d.expiry_date &&
        new Date(d.expiry_date) > now &&
        new Date(d.expiry_date) <= thirtyDaysFromNow
      ).length,
      expired: documents.filter(d =>
        d.expiry_date &&
        new Date(d.expiry_date) < now
      ).length,
      byType,
    }
  },

  // =====================================================
  // Document Versioning Methods
  // =====================================================

  /**
   * Get version history for a document
   */
  async getVersionHistory(documentId: string): Promise<DocumentVersionWithUploader[]> {
    const { data, error } = await supabase
      .from('document_versions')
      .select(`
        *,
        uploader:profiles!document_versions_uploaded_by_fkey (
          id,
          full_name,
          avatar_url
        )
      `)
      .eq('document_id', documentId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching version history:', error)
      return []
    }

    return (data ?? []) as DocumentVersionWithUploader[]
  },

  /**
   * Upload a new version of a document
   */
  async uploadNewVersion(
    documentId: string,
    file: File,
    changeNotes: string
  ): Promise<DocumentVersion | null> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) throw new Error('No organization found')

    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('No authenticated user')

    // Get the current document to find its details
    const document = await this.getDocument(documentId)
    if (!document) throw new Error('Document not found')

    // Get current version history to determine next version number
    const versions = await this.getVersionHistory(documentId)
    const currentVersion = versions.find(v => v.is_current)
    const nextVersionNumber = this.calculateNextVersion(currentVersion?.version_number || '1.0')

    // Upload the new file
    const fileParts = file.name.split('.')
    const fileExt = fileParts.length > 1 ? fileParts.pop() || 'bin' : 'bin'
    const fileName = `${orgId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`

    const { error: uploadError } = await storage.uploadDocument(DOCUMENTS_BUCKET, fileName, file)
    if (uploadError) {
      console.error('Error uploading new version file:', uploadError)
      throw toError(uploadError)
    }

    // Start a transaction-like flow: mark old version as not current, insert new version
    // First, unset is_current on all existing versions for this document
    const { error: updateError } = await supabase
      .from('document_versions')
      .update({ is_current: false })
      .eq('document_id', documentId)
      .eq('is_current', true)

    if (updateError) {
      // Clean up uploaded file
      await storage.deleteDocument(DOCUMENTS_BUCKET, [fileName])
      console.error('Error updating current version:', updateError)
      throw toError(updateError)
    }

    // Insert the new version record
    const { data: newVersion, error: insertError } = await supabase
      .from('document_versions')
      .insert({
        document_id: documentId,
        version_number: nextVersionNumber,
        file_path: fileName,
        file_size: file.size,
        change_notes: changeNotes,
        uploaded_by: user.id,
        is_current: true,
      })
      .select()
      .single()

    if (insertError) {
      // Compensate: re-mark the previous current version to avoid orphaned state
      try {
        if (currentVersion) {
          await supabase
            .from('document_versions')
            .update({ is_current: true })
            .eq('id', currentVersion.id)
        }
        // Clean up uploaded file
        await storage.deleteDocument(DOCUMENTS_BUCKET, [fileName])
      } catch (rollbackError) {
        console.error('Rollback for document version creation failed. Manual intervention may be required.', rollbackError)
        captureError(rollbackError, { operation: 'uploadNewVersion_rollback', documentId })
      }
      console.error('Error creating version record:', insertError)
      throw toError(insertError)
    }

    // Update the main document with new file info and version
    const { error: updateError2 } = await supabase
      .from('documents')
      .update({
        file_path: fileName,
        file_size: file.size,
        mime_type: file.type,
        version: nextVersionNumber,
        updated_at: new Date().toISOString(),
      })
      .eq('id', documentId)

    if (updateError2) {
      // Compensate: revert the new version to non-current and restore the old one
      try {
        await supabase
          .from('document_versions')
          .update({ is_current: false })
          .eq('id', (newVersion as DocumentVersion).id)
        if (currentVersion) {
          await supabase
            .from('document_versions')
            .update({ is_current: true })
            .eq('id', currentVersion.id)
        }
      } catch (rollbackError) {
        console.error('Rollback for document update failed. Manual intervention may be required.', rollbackError)
        captureError(rollbackError, { operation: 'uploadNewVersion_docUpdate_rollback', documentId })
      }
      console.error('Error updating document with new version info:', updateError2)
      throw toError(updateError2)
    }

    return newVersion as DocumentVersion
  },

  /**
   * Set a specific version as the current version
   */
  async setCurrentVersion(documentId: string, versionId: string): Promise<void> {
    // Get the version to make current
    const { data: targetVersion, error: fetchError } = await supabase
      .from('document_versions')
      .select('*')
      .eq('id', versionId)
      .eq('document_id', documentId)
      .single()

    if (fetchError || !targetVersion) {
      throw new Error('Version not found')
    }

    // Capture the current version ID before any mutations for rollback
    const { data: previousCurrent } = await supabase
      .from('document_versions')
      .select('id')
      .eq('document_id', documentId)
      .eq('is_current', true)
      .maybeSingle()

    const previousCurrentId = previousCurrent?.id

    // Unset current on all versions for this document
    const { error: updateError } = await supabase
      .from('document_versions')
      .update({ is_current: false })
      .eq('document_id', documentId)
      .eq('is_current', true)

    if (updateError) {
      throw toError(updateError)
    }

    // Set the target version as current
    const { error: setCurrentError } = await supabase
      .from('document_versions')
      .update({ is_current: true })
      .eq('id', versionId)

    if (setCurrentError) {
      throw toError(setCurrentError)
    }

    // Update the main document to point to this version's file
    const { error: docUpdateError } = await supabase
      .from('documents')
      .update({
        file_path: targetVersion.file_path,
        file_size: targetVersion.file_size,
        version: targetVersion.version_number,
        updated_at: new Date().toISOString(),
      })
      .eq('id', documentId)

    if (docUpdateError) {
      // Compensate: revert version flags using the exact previous current version
      try {
        await supabase
          .from('document_versions')
          .update({ is_current: false })
          .eq('id', versionId)
        if (previousCurrentId) {
          await supabase
            .from('document_versions')
            .update({ is_current: true })
            .eq('id', previousCurrentId)
        }
      } catch (rollbackError) {
        console.error('Rollback for setting current version failed. Manual intervention may be required.', rollbackError)
        captureError(rollbackError, { operation: 'setCurrentVersion_rollback', documentId, versionId })
      }
      console.error('Error updating document to new version:', docUpdateError)
      throw toError(docUpdateError)
    }
  },

  /**
   * Download a specific version of a document
   */
  async downloadVersion(versionId: string): Promise<Blob | null> {
    const { data: version, error: fetchError } = await supabase
      .from('document_versions')
      .select('file_path')
      .eq('id', versionId)
      .single()

    if (fetchError || !version?.file_path) {
      console.error('Error fetching version for download:', fetchError)
      return null
    }

    const { data, error } = await storage.downloadDocument(DOCUMENTS_BUCKET, version.file_path)

    if (error) {
      console.error('Error downloading version:', error)
      return null
    }

    return data
  },

  /**
   * Calculate the next version number (simple increment)
   */
  calculateNextVersion(currentVersion: string): string {
    const parts = currentVersion.split('.')
    if (parts.length >= 2) {
      const minor = parseInt(parts[1], 10)
      if (!isNaN(minor)) {
        return `${parts[0]}.${minor + 1}`
      }
    }
    // Fallback: just append .1
    return `${currentVersion}.1`
  },
}
